import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiService } from "../services/api";

const JDInstantTrips: React.FC = () => {
  const [trips, setTrips] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("pending");
  const [rejectTripId, setRejectTripId] = useState<string | null>(null);
  const [rejectRemarks, setRejectRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadTrips = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiService.getJdInstantTrips({
        page: 1,
        limit: 50,
        approvalStatus: filter || undefined,
      });
      if (response.success) {
        const payload = (response as any).data || response;
        setTrips(payload.items || []);
      } else {
        setError(response.message || "Failed to load JD Instant trips");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load JD Instant trips");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadTrips();
  }, [loadTrips]);

  const handleAccept = async (tripId: string) => {
    setSubmitting(true);
    try {
      const response = await apiService.respondToJdInstantTrip(tripId, {
        action: "accept",
      });
      if (!response.success) {
        throw new Error(response.message || "Failed to accept trip");
      }
      await loadTrips();
    } catch (err: any) {
      setError(err?.message || "Failed to accept trip");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectTripId) return;
    const remarks = rejectRemarks.trim();
    if (!remarks) {
      setError("Remarks are required when rejecting a trip");
      return;
    }
    setSubmitting(true);
    try {
      const response = await apiService.respondToJdInstantTrip(rejectTripId, {
        action: "reject",
        remarks,
      });
      if (!response.success) {
        throw new Error(response.message || "Failed to reject trip");
      }
      setRejectTripId(null);
      setRejectRemarks("");
      await loadTrips();
    } catch (err: any) {
      setError(err?.message || "Failed to reject trip");
    } finally {
      setSubmitting(false);
    }
  };

  const formatSchedule = (trip: any) => {
    if (trip.scheduledAt) {
      return new Date(trip.scheduledAt).toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      });
    }
    return [trip.pickupDate, trip.pickupTime].filter(Boolean).join(" ") || "—";
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">JD Instant</h1>
          <p className="text-sm text-gray-500">
            Review assigned trips and accept or reject them.
          </p>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="pending">Pending approval</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
          <option value="">All</option>
        </select>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Trip ID</th>
                <th className="px-4 py-3">Schedule</th>
                <th className="px-4 py-3">Route</th>
                <th className="px-4 py-3">Vehicle</th>
                <th className="px-4 py-3">Driver</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                    Loading trips...
                  </td>
                </tr>
              ) : trips.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                    No trips found
                  </td>
                </tr>
              ) : (
                trips.map((trip) => (
                  <tr key={trip._id}>
                    <td className="px-4 py-4 font-medium text-gray-900">
                      <Link
                        to={`/jd-instant/${trip._id}`}
                        className="text-jd-green hover:underline"
                      >
                        {trip.tripId || "—"}
                      </Link>
                    </td>
                    <td className="px-4 py-4 text-gray-700">{formatSchedule(trip)}</td>
                    <td className="px-4 py-4 text-gray-700 max-w-xs">
                      <div className="line-clamp-2">
                        {trip.startPoint?.address || "—"}
                        {trip.endPoint?.address ? ` → ${trip.endPoint.address}` : ""}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-gray-700">
                      {trip.driverDetails?.vehicleNumber || "—"}
                    </td>
                    <td className="px-4 py-4 text-gray-700">
                      {trip.driverDetails?.name || "—"}
                    </td>
                    <td className="px-4 py-4 capitalize text-gray-700">
                      {trip.vendorApproval?.status || "—"}
                    </td>
                    <td className="px-4 py-4">
                      {trip.vendorApproval?.status === "pending" ? (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={submitting}
                            onClick={() => handleAccept(trip._id)}
                            className="rounded-lg bg-jd-green px-3 py-2 text-xs font-medium text-white hover:opacity-90 disabled:opacity-60"
                          >
                            Accept
                          </button>
                          <button
                            type="button"
                            disabled={submitting}
                            onClick={() => {
                              setRejectTripId(trip._id);
                              setRejectRemarks("");
                              setError("");
                            }}
                            className="rounded-lg border border-red-300 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
                          >
                            Reject
                          </button>
                        </div>
                      ) : trip.vendorApproval?.status === "rejected" ? (
                        <span className="text-xs text-red-700">
                          {trip.vendorApproval?.remarks || "Rejected"}
                        </span>
                      ) : (
                        <span className="text-xs text-green-700">Approved</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {rejectTripId ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
            <h2 className="text-lg font-semibold text-gray-900">Reject trip</h2>
            <p className="mt-1 text-sm text-gray-500">
              Please share why you are rejecting this trip.
            </p>
            <textarea
              value={rejectRemarks}
              onChange={(e) => setRejectRemarks(e.target.value)}
              rows={4}
              className="mt-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Enter remarks"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setRejectTripId(null);
                  setRejectRemarks("");
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={handleReject}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                Reject trip
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default JDInstantTrips;
